import { FC, useState, useEffect } from "react";
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Dropdown, Field, MessageBar, MessageBarBody, Option, Spinner, Text } from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import { getUserGroupsByModuleAndOrganizationID } from "../../../services/userGroupService";
import { User } from "../../../models/users";
import { useAppContext } from "../../../AppContext";

interface Props {
  onClose: () => void;
  open: boolean;
  onMove: (userGroupID: number, userGroupName: string, users: User[]) => void;
  moduleID: number;
  organizationID: number;
  currentUserGroupID: number;
  currentUserGroupName: string;
  isUserGroupAdmin: boolean;
}

const MovePlacement: FC<Props> = ({ onClose, open, onMove, moduleID, organizationID, currentUserGroupID, currentUserGroupName, isUserGroupAdmin }) => {
  const { user: loggedUser } = useAppContext();
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const [selectedUserGroupID, setSelectedUserGroupID] = useState<number | null>(null);
  const [selectedUserGroupName, setSelectedUserGroupName] = useState<string>("");
  const [step, setStep] = useState<'group' | 'users'>('group');
  const [loadingGroups, setLoadingGroups] = useState<boolean>(false);
  const [groupError, setGroupError] = useState<string>("");
  const [groupUsers, setGroupUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userError, setUserError] = useState<string>("");
  const [isMoving, setIsMoving] = useState<boolean>(false);

  const handleReset = () => {
    setSelectedUserGroupID(null);
    setSelectedUserGroupName("");
    setGroupError("");
    setGroupUsers([]);
    setSelectedUser(null);
    setUserError("");
    // Group admins choose a new group first; regular members go straight to picking users within their current group
    setStep(isUserGroupAdmin ? 'group' : 'users');
  };

  const handleMove = async () => {
    if (!selectedUser) {
      setUserError("En användare krävs");
      return;
    }

    setIsMoving(true);
    try {
      await onMove(selectedUserGroupID!, selectedUserGroupName, [selectedUser]);
      handleReset();
      onClose();
    } catch (error) {
      console.error('Error moving placement:', error);
    } finally {
      setIsMoving(false);
    }
  };

  useEffect(() => {
    if (!open) {
      handleReset();
      return;
    }

    // Load the user groups within this tendering that the placement could be moved to
    const loadUserGroups = async () => {
      setLoadingGroups(true);
      try {
        const groups = await getUserGroupsByModuleAndOrganizationID(moduleID, organizationID);
        setUserGroups(groups);

        if (!isUserGroupAdmin) {
          // Regular members stay within the placement's current user group
          const currentGroup = groups.find((g: any) => g.userGroupID === currentUserGroupID);
          setSelectedUserGroupID(currentUserGroupID);
          setSelectedUserGroupName(currentUserGroupName);
          // Only Kommunanvändare can own a placement; Behörighetsansvarig-only members can't be selected
          setGroupUsers((currentGroup?.userGroupAssignments ?? [])
            .filter((a: any) => a.roles?.some((r: any) => r.roleName === "Kommunanvändare"))
            .map((a: any) => a.user));
        }
      } catch (error) {
        console.error('Error fetching user groups:', error);
        setUserGroups([]);
      } finally {
        setLoadingGroups(false);
      }
    };

    loadUserGroups();
  }, [open, moduleID, organizationID, currentUserGroupID, currentUserGroupName, isUserGroupAdmin]);

  // Admins may also pick the placement's current group, just without themselves as a recipient
  const selectableUserGroups = userGroups;
  const availableUsers = groupUsers.filter((u) => u.userID !== loggedUser?.user.userID && u.userID !== selectedUser?.userID);

  const handleRemoveUser = () => {
    setSelectedUser(null);
  };

  const handleContinueToUsers = () => {
    const selectedGroup = userGroups.find((g: any) => g.userGroupID === selectedUserGroupID);
    if (!selectedUserGroupID || !selectedGroup) {
      setGroupError("Grupp krävs");
      return;
    }

    setSelectedUserGroupName(selectedGroup.userGroupName);
    // Only Kommunanvändare can own a placement; Behörighetsansvarig-only members can't be selected
    setGroupUsers((selectedGroup.userGroupAssignments ?? [])
      .filter((a: any) => a.roles?.some((r: any) => r.roleName === "Kommunanvändare"))
      .map((a: any) => a.user));
    setSelectedUser(null);
    setUserError("");
    setStep('users');
  };

  return (
    <Dialog open={open} onOpenChange={(_event, data) => !data.open && onClose()}>
      <DialogSurface style={{ width: "600px", maxWidth: "600px", height: "600px" }}>
        <DialogBody style={{ height: "100%" }}>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                shape="circular"
                aria-label="close"
                icon={<DismissRegular />}
                onClick={onClose}
              />
            }
          >
            Flytta förfrågan
          </DialogTitle>
          <DialogContent style={{ flexGrow: 1, overflowY: "auto" }}>
            <MessageBar intent="info" className="mb-3">
              <MessageBarBody>
                Din egen tillgång till förfrågan tas bort när flytten genomförs. De användare du väljer nedan får tillgång istället.
              </MessageBarBody>
            </MessageBar>
            {step === 'group' ? (
              <>
                <Text>
                  Välj den grupp som ska få tillgång till denna förfrågan:
                </Text>
                <Field
                  label="Grupp"
                  validationMessage={groupError}
                  validationState={groupError ? "error" : "none"}
                  required
                  className="mt-3"
                >
                  <Dropdown
                    placeholder={loadingGroups ? "Laddar..." : "Välj en grupp"}
                    disabled={loadingGroups}
                    value={selectableUserGroups.find(ug => ug.userGroupID === selectedUserGroupID)?.userGroupName ?? ""}
                    // DialogContent scrolls internally, which otherwise clips the popup's available height to the dialog's bounds
                    positioning={{ overflowBoundary: 'window' }}
                    listbox={{ style: { maxHeight: '250px', overflowY: 'auto' } }}
                    onOptionSelect={(_, data) => {
                      setSelectedUserGroupID(Number(data.optionValue));
                      setGroupError("");
                    }}
                  >
                    {selectableUserGroups.map((ug) => (
                      <Option key={ug.userGroupID} value={ug.userGroupID.toString()}>
                        {ug.userGroupName}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>
              </>
            ) : (
              <>
                <Text>
                  Välj den användare inom {selectedUserGroupName} som ska få tillgång till denna förfrågan:
                </Text>
                <Field
                  label="Användare"
                  validationMessage={userError}
                  validationState={userError ? "error" : "none"}
                  required
                  className="mt-3"
                >
                  <div className="flex flex-wrap gap-2 border border-gray-300 rounded-md p-1 min-h-[36px]">
                    {selectedUser && (
                      <div className="flex items-center gap-1 px-2 py-1 rounded-full text-sm bg-blue-100 text-blue-700">
                        <span>{selectedUser.firstName} {selectedUser.lastName} ({selectedUser.email})</span>
                        <Button
                          appearance="transparent"
                          size="small"
                          icon={<DismissRegular />}
                          onClick={handleRemoveUser}
                        />
                      </div>
                    )}
                  </div>
                </Field>
                <Field label="Lägg till användare från gruppen" className="mt-3">
                  <Dropdown
                    placeholder="Välj en användare"
                    value=""
                    selectedOptions={[]}
                    // DialogContent scrolls internally, which otherwise clips the popup's available height to the dialog's bounds
                    positioning={{ overflowBoundary: 'window' }}
                    listbox={{ style: { maxHeight: '250px', overflowY: 'auto' } }}
                    onOptionSelect={(_, data) => {
                      const u = availableUsers.find((u) => u.userID.toString() === data.optionValue);
                      if (u) {
                        setSelectedUser(u);
                        setUserError("");
                      }
                    }}
                  >
                    {availableUsers.length === 0 ? (
                      <Option key="none" value="none" disabled text="Inga användare att välja">
                        Inga användare att välja
                      </Option>
                    ) : (
                      availableUsers.map((u) => (
                        <Option key={u.userID} value={u.userID.toString()} text={`${u.firstName} ${u.lastName}`}>
                          {u.firstName} {u.lastName} ({u.email})
                        </Option>
                      ))
                    )}
                  </Dropdown>
                </Field>
                {isUserGroupAdmin && (
                  <Button
                    appearance="subtle"
                    onClick={() => setStep('group')}
                    className="mt-2"
                  >
                    Byt grupp
                  </Button>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={isMoving}>Avbryt</Button>
            <Button
              appearance="primary"
              onClick={step === 'group' ? handleContinueToUsers : handleMove}
              disabled={isMoving || (step === 'group' ? !selectedUserGroupID : !selectedUser)}
              icon={isMoving ? <Spinner size="tiny" /> : undefined}
            >
              {isMoving ? 'Bearbetar...' : step === 'group' ? 'Nästa' : 'Spara'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default MovePlacement;
